import React, { useEffect, useContext } from 'react';
import {
  Badge, Drawer, List, ListItem, ListItemIcon, ListItemText, Divider,
  IconButton, Avatar, Typography, Box, ListSubheader
} from '@mui/material';
import { styled } from '@mui/system';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import HandymanIcon from '@mui/icons-material/Handyman';
import SyncIcon from '@mui/icons-material/Sync';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LoginIcon from '@mui/icons-material/Login';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EuroIcon from '@mui/icons-material/Euro';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import HubIcon from '@mui/icons-material/Hub';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useState } from 'react';
import { Link } from "react-router-dom"
import AppContext from './AppContext';
import packageJson from '../../package.json';
import axios from 'axios';
import config from '../config.js';

const InversioIcon = ({ BaseIcon }) => (
  <Box sx={{ position: 'relative', display: 'inline-flex', width: 24, height: 24, alignItems: 'center', justifyContent: 'center' }}>
    <BaseIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
    <TrendingUpIcon
      sx={{
        position: 'absolute',
        bottom: -2,
        right: -2,
        fontSize: 13,
        color: 'primary.main',
        backgroundColor: 'white',
        borderRadius: '50%',
        boxShadow: '0 0 2px rgba(0,0,0,0.2)',
        padding: '1px'
      }}
    />
  </Box>
);

const CompteIcon = ({ BaseIcon }) => (
  <Box sx={{ position: 'relative', display: 'inline-flex', width: 24, height: 24, alignItems: 'center', justifyContent: 'center' }}>
    <BaseIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
    <EuroIcon
      sx={{
        position: 'absolute',
        bottom: -2,
        right: -2,
        fontSize: 13,
        color: 'success.main',
        backgroundColor: 'white',
        borderRadius: '50%',
        boxShadow: '0 0 2px rgba(0,0,0,0.2)',
        padding: '1px'
      }}
    />
  </Box>
);

const HeaderContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  color: 'white',
  backgroundImage: `url(${process.env.PUBLIC_URL}/material_background3.png)`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
});

const SidebarMenu = ({ open, onClose }) => {
  const { ctxApp, ctxUser } = useContext(AppContext);
  const [drawerOpen, setDrawerOpen] = useState(open);
  const [numBolita, setNumBolita] = useState(0);

  const [hasInversions, setHasInversions] = useState(false);

  useEffect(() => {
    setDrawerOpen(open);

    const num = (ctxApp.numPendentTotal) || 0;
    setNumBolita(num);
    // eslint-disable-next-line
  }, [open, ctxApp.numPendentTotal]);

  useEffect(() => {
    const jwtToken = ctxUser?.fdvToken?.jwtToken || '';
    if (ctxApp?.userapp && jwtToken) {
      axios.get(`${config.apiBaseUrl}/v1/resumglobalinversio/${ctxApp.userapp}`, {
        headers: { Authorization: `${jwtToken}` }
      })
      .then(response => {
        const hasAssets = response.data?.actius && response.data.actius.length > 0;
        setHasInversions(hasAssets);
      })
      .catch(err => {
        console.error("Error al comprovar inversions del compte", err);
        setHasInversions(false);
      });
    } else {
      setHasInversions(false);
    }
  }, [ctxApp?.userapp, ctxUser]);

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    if (onClose) onClose();
  };

  const getUrlMesActual = () => {
    const dt = new Date();
    const currentYear = dt.getFullYear();
    const currentMonth = dt.getMonth() + 1;
    return `/${ctxApp.userapp}/dashboard/${currentYear}/mes/${currentMonth}`;
  };

  const getUrlInversioAnyActual = () => {
    const dt = new Date();
    return `/${ctxApp.userapp}/inversio/${dt.getFullYear()}`;
  };

  const getUrlInversioMesActual = () => {
    const dt = new Date();
    const currentYear = dt.getFullYear();
    const currentMonth = dt.getMonth() + 1;
    return `/${ctxApp.userapp}/inversio/${currentYear}/mes/${currentMonth}`;
  };

  const username = ctxUser?.decodedToken?.name || 'Invitado';
  const imageUrl = ctxUser?.decodedToken?.picture || '';

  return (
    <Drawer open={drawerOpen} onClose={handleDrawerClose}>
      <div style={{ width: 250, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px' }}>
          <IconButton onClick={handleDrawerClose}>
            <ChevronLeftIcon />
          </IconButton>
        </div>
        <HeaderContainer>
          <Avatar
            src={imageUrl}
            alt={username}
            sx={{ width: 80, height: 80, border: '2px solid white', mb: 1 }}
          >
            {!imageUrl && <AccountCircleIcon sx={{ fontSize: 80 }} />}
          </Avatar>
          <Typography variant="h6">{username}</Typography>
        </HeaderContainer>
        <Divider />

        <List>
          <ListSubheader component="div" sx={{ background: 'white', fontWeight: 'bold', lineHeight: '30px' }}>
            Comptes i Estalvi
          </ListSubheader>

          {/* Tots els anys */}
          <ListItem
            button
            //onClick={()=>handleNavigate(`/${ctxApp.userapp}/dashboard`)}
            onClick={handleDrawerClose}
            component={Link}
            to={`/${ctxApp.userapp}/dashboard`}
          >
            <ListItemIcon>
              <CompteIcon BaseIcon={CalendarViewMonthIcon} />
            </ListItemIcon>
            <ListItemText primary="Tots els anys" />
          </ListItem>

          {/* Año actual */}
          <ListItem
            button
            //onClick={()=>handleNavigate('/')}
            onClick={handleDrawerClose}
            component={Link}
            to={`/`}
          >
            <ListItemIcon>
              <CompteIcon BaseIcon={CalendarTodayIcon} />
            </ListItemIcon>
            <ListItemText primary="Any actual" />
          </ListItem>

          {/* Mes actual */}
          <ListItem
            button
            //onClick={()=>gotoMesActual()}
            onClick={handleDrawerClose}
            component={Link}
            to={getUrlMesActual()}
          >
            <ListItemIcon>
              <CompteIcon BaseIcon={EventNoteIcon} />
            </ListItemIcon>
            <ListItemText primary="Mes actual" />
          </ListItem>

          {/* Tratamiento manual */}
          <ListItem
            button
            //onClick={()=>handleNavigate(`/${ctxApp.userapp}/tractamentmanual`)}
            onClick={handleDrawerClose}
            component={Link}
            to={`/${ctxApp.userapp}/tractamentmanual`}
          >
            <ListItemIcon>
              <Badge
                color="error" // Esto muestra la bolita en color rojo
                //variant="dot" // Usa "dot" para que sea una bolita sin número
                badgeContent={numBolita}
                invisible={numBolita === 0} // Solo se muestra si hay notificaciones
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <CompteIcon BaseIcon={HandymanIcon} />
              </Badge>
            </ListItemIcon>
            <ListItemText primary="Tractament manual" />
          </ListItem>

          {/* Cercador Moviments */}
          <ListItem
            button
            onClick={handleDrawerClose}
            component={Link}
            to={`/${ctxApp.userapp}/cercadormovs`}
          >
            <ListItemIcon>
              <CompteIcon BaseIcon={SearchIcon} />
            </ListItemIcon>
            <ListItemText primary="Cercador Moviments" />
          </ListItem>

          {/* Resum Jeràrquic */}
          <ListItem
            button
            onClick={handleDrawerClose}
            component={Link}
            to={`/${ctxApp.userapp}/jerarquic`}
          >
            <ListItemIcon>
              <CompteIcon BaseIcon={AccountTreeIcon} />
            </ListItemIcon>
            <ListItemText primary="Resum Jeràrquic" />
          </ListItem>

          {/* Resum Transversal (Vehicles i Persones) */}
          <ListItem
            button
            onClick={handleDrawerClose}
            component={Link}
            to={`/${ctxApp.userapp}/transversal`}
          >
            <ListItemIcon>
              <CompteIcon BaseIcon={HubIcon} />
            </ListItemIcon>
            <ListItemText primary="Vehicles i Membres" />
          </ListItem>

          {/* Refinament Jeràrquic */}
          <ListItem
            button
            onClick={handleDrawerClose}
            component={Link}
            to={`/${ctxApp.userapp}/refinament`}
          >
            <ListItemIcon>
              <CompteIcon BaseIcon={AutoFixHighIcon} />
            </ListItemIcon>
            <ListItemText primary="Refinament Jeràrquic" />
          </ListItem>

          {/* Sincronización */}
          <ListItem
            button
            onClick={handleDrawerClose}
            component={Link}
            to={`/${ctxApp.userapp}/sincrobancaria`}
          >
            <ListItemIcon>
              <CompteIcon BaseIcon={SyncIcon} />
            </ListItemIcon>
            <ListItemText primary="Sincronització" />
          </ListItem>

          {hasInversions && (
            <>
              <Divider sx={{ my: 1 }} />

              <ListSubheader component="div" sx={{ background: 'white', fontWeight: 'bold', lineHeight: '30px' }}>
                Inversions
              </ListSubheader>

              {/* Resum Global Inversió */}
              <ListItem
                button
                onClick={handleDrawerClose}
                component={Link}
                to={`/${ctxApp.userapp}/inversio`}
              >
                <ListItemIcon>
                  <InversioIcon BaseIcon={CalendarViewMonthIcon} />
                </ListItemIcon>
                <ListItemText primary="Tots els anys" />
              </ListItem>

              {/* Any actual Inversió */}
              <ListItem
                button
                onClick={handleDrawerClose}
                component={Link}
                to={getUrlInversioAnyActual()}
              >
                <ListItemIcon>
                  <InversioIcon BaseIcon={CalendarTodayIcon} />
                </ListItemIcon>
                <ListItemText primary="Any actual" />
              </ListItem>

              {/* Mes actual Inversió */}
              <ListItem
                button
                onClick={handleDrawerClose}
                component={Link}
                to={getUrlInversioMesActual()}
              >
                <ListItemIcon>
                  <InversioIcon BaseIcon={EventNoteIcon} />
                </ListItemIcon>
                <ListItemText primary="Mes actual" />
              </ListItem>

              {/* Sincronització Inversions */}
              <ListItem
                button
                onClick={handleDrawerClose}
                component={Link}
                to={`/${ctxApp.userapp}/sincroinversions`}
              >
                <ListItemIcon>
                  <InversioIcon BaseIcon={SyncIcon} />
                </ListItemIcon>
                <ListItemText primary="Sincronització" />
              </ListItem>
            </>
          )}

          <Divider sx={{ my: 1 }} />

          <ListSubheader component="div" sx={{ background: 'white', fontWeight: 'bold', lineHeight: '30px' }}>
            Sistema
          </ListSubheader>

          {/* Test Login */}
          <ListItem
            button
            //onClick={()=>handleNavigate(`/testlogin`)}
            onClick={handleDrawerClose}
            component={Link}
            to={`/testlogin`}

          >
            <ListItemIcon>
              <LoginIcon />
            </ListItemIcon>
            <ListItemText primary="Test Login" />
          </ListItem>

          {/* Gestión de datos */}
          <ListItem
            button
            onClick={handleDrawerClose}
            component={Link}
            to={`/${ctxApp.userapp}/gestiodades`}
          >
            <ListItemIcon>
              <DataUsageIcon />
            </ListItemIcon>
            <ListItemText primary="Gestió de dades" />
          </ListItem>

          {/* Configuración */}
          <ListItem
            button
            onClick={handleDrawerClose}
            component={Link}
            to={`/${ctxApp.userapp}/sincroconfig`}
          >
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Configuració" />
          </ListItem>

          {/* Logout */}
          <ListItem
            button
            //onClick={()=>handleNavigate('/logout')}
            onClick={handleDrawerClose}
            component={Link}
            to={'/logout'}

          >
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItem>
        </List>

        {/* Footer Version */}
        <Box sx={{ marginTop: 'auto', padding: 2, textAlign: 'center' }}>

          <Typography variant="caption" display="block" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            Versión: {packageJson.version}
          </Typography>
        </Box>
      </div>
    </Drawer>
  );
};

export default SidebarMenu;
